import { Select, TextInput } from '../ui/Modal';

/**
 * A date input plus three explicit 12-hour controls — Hour / Minute / AM-PM —
 * in place of the browser's native `<input type="datetime-local">`.
 *
 * WHY NOT THE NATIVE PICKER. `type="datetime-local"` renders in whatever
 * 12-/24-hour convention the BROWSER's locale settings say, not what this
 * product wants shown — on a 24-hour-locale machine it shows 24-hour time
 * with no way to ask it for AM/PM. Three plain `<select>`s sidestep the
 * locale entirely: "2:30 PM" reads the same regardless of what the browser
 * or OS is configured to.
 *
 * STILL A NAIVE WALL-CLOCK STRING. This produces and consumes the exact same
 * `YYYY-MM-DDTHH:mm` this field always stored — see `VoucherTransportLegFields`'s
 * own doc comment on why that never goes through `Date`/`toISOString()`. This
 * component is a different INPUT WIDGET for the same wire value, not a
 * behaviour change: no timezone conversion happens here either.
 */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0..59

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

interface WallClockParts {
  date: string;
  hour12: number;
  minute: number;
  period: 'AM' | 'PM';
}

/** `''` (or anything not matching the wire shape) means "nothing picked yet". */
function parseWallClock(value: string): WallClockParts | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  // Non-null: all three groups are mandatory in the pattern above, so a match
  // guarantees every one of them captured something.
  const date = match[1]!;
  const hh24 = Number(match[2]!);
  const minute = Number(match[3]!);
  const period: 'AM' | 'PM' = hh24 >= 12 ? 'PM' : 'AM';
  const hour12 = hh24 % 12 === 0 ? 12 : hh24 % 12;
  return { date, hour12, minute, period };
}

function composeWallClock(parts: WallClockParts): string {
  const hh24 = parts.period === 'AM' ? parts.hour12 % 12 : (parts.hour12 % 12) + 12;
  return `${parts.date}T${pad2(hh24)}:${pad2(parts.minute)}`;
}

export function WallClockPicker({
  label,
  value,
  onChange,
  required,
  invalid,
}: {
  /**
   * The field this picker is filling in, e.g. "Leg 1 departure" — the
   * `Field` wrapper's own visible label describes the GROUP, but each of the
   * four controls below carries its own explicit `aria-label` (so a screen
   * reader can tell a hour `<select>` apart from a minute one), which means
   * none of them inherit that wrapping label text. Without this prop every
   * leg's every hour control would announce as the same bare "Hour", with no
   * way to tell which leg or which of departure/arrival it belongs to.
   */
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  invalid?: boolean;
}): JSX.Element {
  const parts = parseWallClock(value);

  /**
   * Picking any one of the four parts before the others are set defaults the
   * REST to a sensible start (midnight) rather than silently discarding the
   * one part the user just picked — a date-only leg is a real, useful
   * intermediate state while someone fills in the rest of the row.
   */
  function patch(next: Partial<WallClockParts>): void {
    const base: WallClockParts = parts ?? { date: '', hour12: 12, minute: 0, period: 'AM' };
    const merged = { ...base, ...next };
    if (merged.date === '') {
      onChange('');
      return;
    }
    onChange(composeWallClock(merged));
  }

  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-1.5">
      <TextInput
        type="date"
        aria-label={`${label} date`}
        required={required}
        aria-invalid={invalid}
        value={parts?.date ?? ''}
        onChange={(e) => {
          patch({ date: e.target.value });
        }}
      />
      <Select
        aria-label={`${label} hour`}
        className="w-[62px]"
        value={parts === null ? '' : String(parts.hour12)}
        disabled={parts === null}
        onChange={(e) => {
          patch({ hour12: Number(e.target.value) });
        }}
      >
        {HOURS_12.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </Select>
      <Select
        aria-label={`${label} minute`}
        className="w-[62px]"
        value={parts === null ? '' : pad2(parts.minute)}
        disabled={parts === null}
        onChange={(e) => {
          patch({ minute: Number(e.target.value) });
        }}
      >
        {MINUTES.map((m) => (
          <option key={m} value={pad2(m)}>
            {pad2(m)}
          </option>
        ))}
      </Select>
      <Select
        aria-label={`${label} AM or PM`}
        className="w-[62px]"
        value={parts?.period ?? ''}
        disabled={parts === null}
        onChange={(e) => {
          patch({ period: e.target.value as 'AM' | 'PM' });
        }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </Select>
    </div>
  );
}
